import type { MouseEventHandler, ReactNode } from "react";
import { Button } from "@design-system/Button/Button";
import { MenuMore } from "@design-system/Menu/MenuMore";
import type { MenuMoreProps } from "@design-system/Menu/Menu.types";
import { useCellControlTabIndex } from "./cellControls";
import styles from "./ActionsCell.module.scss";

/** `hover` shows the control on row hover, focus, or active cell; `always` keeps it visible. */
export type ActionReveal = "hover" | "always";

interface ActionsCellRootProps {
    children: ReactNode;
    /** `start` for a display column that holds a control (a status link); defaults to `end`. */
    align?: "start" | "end";
}

interface ActionSlotProps {
    reveal?: ActionReveal;
    children: ReactNode;
}

interface ActionsCellButtonProps {
    children: ReactNode;
    onClick: MouseEventHandler<HTMLButtonElement>;
    variant?: "primary" | "secondary";
    disabled?: boolean;
    reveal?: ActionReveal;
}

interface ActionsCellIconButtonProps {
    icon: ReactNode;
    /** Follows the copy vocabulary: "Delete document", "Mark as open". */
    "aria-label": string;
    onClick: MouseEventHandler<HTMLButtonElement>;
    disabled?: boolean;
    reveal?: ActionReveal;
}

type ActionsCellMenuProps = Omit<
    MenuMoreProps,
    "size" | "variant" | "tabIndex" | "className"
> & { reveal?: ActionReveal };

/**
 * Row actions container: controls in a row, aligned to the cell's end. Use
 * inside an actions column's `render`. `data-dt-controls` tells the grid this
 * cell holds controls it can move focus into.
 *
 * @example
 * const StatementActions = ({ context }: DataGridCellProps<Statement>) => (
 *     <ActionsCell>
 *         <ActionsCell.Button variant="primary" onClick={review}>Review</ActionsCell.Button>
 *         <ActionsCell.Menu>
 *             <Menu.Item onClick={exclude}>Exclude</Menu.Item>
 *         </ActionsCell.Menu>
 *     </ActionsCell>
 * );
 */
const ActionsCellRoot = ({
    children,
    align = "end",
}: Readonly<ActionsCellRootProps>) => (
    <div className={styles.actions} data-align={align} data-dt-controls="">
        {children}
    </div>
);

/** Any content in the actions row (an icon, a status link) with the reveal rule. */
const ActionSlot = ({
    reveal = "hover",
    children,
}: Readonly<ActionSlotProps>) => (
    <span className={styles.slot} data-reveal={reveal}>
        {children}
    </span>
);

/** A labelled `md` button, e.g. "Review". */
const ActionsCellButton = ({
    children,
    onClick,
    variant = "secondary",
    disabled,
    reveal,
}: Readonly<ActionsCellButtonProps>) => {
    const tabIndex = useCellControlTabIndex();
    return (
        <ActionSlot reveal={reveal}>
            <Button
                type="button"
                variant={variant}
                size="md"
                disabled={disabled}
                tabIndex={tabIndex}
                onClick={onClick}
            >
                {children}
            </Button>
        </ActionSlot>
    );
};

/** An icon-only `md` secondary button; `aria-label` is required. */
const ActionsCellIconButton = ({
    icon,
    "aria-label": ariaLabel,
    onClick,
    disabled,
    reveal,
}: Readonly<ActionsCellIconButtonProps>) => {
    const tabIndex = useCellControlTabIndex();
    return (
        <ActionSlot reveal={reveal}>
            <Button
                type="button"
                variant="secondary"
                size="md"
                iconOnly
                aria-label={ariaLabel}
                disabled={disabled}
                tabIndex={tabIndex}
                onClick={onClick}
            >
                {icon}
            </Button>
        </ActionSlot>
    );
};

/** The "Row actions" kebab menu at size `md`; children are `Menu.Item`s. */
const ActionsCellMenu = ({
    reveal,
    "aria-label": ariaLabel = "Row actions",
    ...menuProps
}: Readonly<ActionsCellMenuProps>) => {
    const tabIndex = useCellControlTabIndex();
    return (
        <ActionSlot reveal={reveal}>
            <MenuMore
                {...menuProps}
                aria-label={ariaLabel}
                size="md"
                tabIndex={tabIndex}
            />
        </ActionSlot>
    );
};

export const ActionsCell = Object.assign(ActionsCellRoot, {
    Button: ActionsCellButton,
    IconButton: ActionsCellIconButton,
    Menu: ActionsCellMenu,
    Slot: ActionSlot,
});
